import PropTypes from 'prop-types';
import { Component } from 'react';
import { Alert } from 'react-bootstrap';
import type { KeyboardEvent, MouseEvent, VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { getGitServiceName } from '../../../../shared/projectsV2/vcs';
import { FREE_PLAN_NAME, isFreeTier } from '../../../../shared/subscriptions';
import { ProjectSource } from '../../../../typings/enums';
import { Action } from '../../../../typings/permissions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { window } from '../../../globals';
import { isContainerProject } from '../../../helpers/containers';
import { useIsSecurityEnabled } from '../../../helpers/features';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import { getAuthorizeServiceURL } from '../../../helpers/services';
import useScanRevision from '../../../hooks/api/revisions/useScanRevision';
import { get, put, remove } from '../../../redux/api';
import utils from '../../../utils';
import Button, { ButtonGroup } from '../../generic/Button';
import Flex from '../../generic/Flex';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Icon, { icons } from '../../generic/Icon';
import { DocsLink, Link } from '../../generic/Link';
import Loader from '../../generic/Loader';
import Redirect from '../../generic/Redirect';
import Text from '../../generic/Text';
import Tooltip from '../../generic/Tooltip';
import ModalNoDependenciesFound from '../../modals/ModalNoDependenciesFound';
import ModalStreamBuild from '../../modals/ModalStreamBuild';
import { apiSupportFeature } from '../../settings/billing/features/consts';
import { useCanUserEditProject } from '../settingsv2/permissions';
import getRevisionStatus from '../shared/projectSummary/getRevisionStatus';
import SBOMSummary from '../summary/SBOMSummary';
import type { ProjectDashboardProject, ProjectDashboardRevision, ProjectSummaryProps } from '../projectDashboardTypes';

import ProjectSummaryBuildInfo from './ProjectSummaryBuildInfo';
import ProjectSummaryIssues from './ProjectSummaryIssues';
import { LabelsCard } from './ProjectSummaryV2/InfoCards/LabelsCard';
import { ReleaseGroupsCard } from './ProjectSummaryV2/InfoCards/ReleaseGroupsCard';
import { TeamAccessCard } from './ProjectSummaryV2/InfoCards/TeamAccessCard';
import StandardProjectSummaryActionCards from './StandardProjectSummaryActionCards';

export const LanguageDocs = {
  JavaArtifact: {
    name: 'Java',
    slug: 'java',
  },
  CocoaPodsPackage: {
    name: 'Cocoapods (iOS)',
    slug: 'ios',
  },
  RustCargoPackage: {
    name: 'Rust',
    slug: 'rust',
  },
  CommonJSPackage: {
    name: 'JavaScript',
    slug: 'javascript',
  },
};

type RevisionStatus =
  | 'loading' // Fetching project or revision data.
  | 'analyzingDependencies' // There is a resolved revision. The dependencies are being built.
  | 'analyzed' // There is a resolved revision. Either the issue scan has not ran yet or active issues weren't found.
  | 'failing' // There is a resolved revision. The issue issue scan ran and found active issues.
  | 'unanalyzed'; // The selected revision isn't yet resolved.

type ProjectSummaryState = {
  rebuilding: boolean;
  rebuildBuildId?: number;
  showNoDependenciesModal: boolean;
};

class ProjectSummary extends Component<ProjectSummaryProps, ProjectSummaryState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props: ProjectSummaryProps) {
    super(props);
    this.state = {
      rebuilding: false,
      rebuildBuildId: null,
      showNoDependenciesModal: false,
    };
  }

  onHideModalStreamBuild = () => {
    this.setState({ rebuilding: false });
  };

  getRevisionStatus = (): RevisionStatus => {
    const { revision, project } = this.props;

    return getRevisionStatus(revision, project);
  };

  get userCanEditProject(): boolean {
    return userHasPermissionForProject(Action.Edit, this.props.project.data?.locator);
  }

  generatePublicAttributionLink = async () => {
    const { project, onProjectChange, onAddToast } = this.props;
    await put<void, string>(`/api/projects/${utils.nuc(project?.data?.locator as string)}/generate_attribution_slug`, undefined);
    //  Refresh the project summary page. This will also refresh the public attribution link, since the Project.bom_public_id will have changed.
    onProjectChange();
    //  Let the user know it was an overwhelming success.
    onAddToast({
      type: 'success',
      content: 'Public report link generated.',
    });
  };

  deletePublicAttributionLink = async () => {
    const { project, onProjectChange, onAddToast } = this.props;
    await remove(`/api/projects/${utils.nuc(project?.data?.locator as string)}/generate_attribution_slug`, undefined);
    //  Refresh the project summary page. This will also refresh the public attribution link, since the Project.bom_public_id will have changed.
    onProjectChange();
    //  Let the user know it was an overwhelming success.
    onAddToast({
      type: 'success',
      content: 'Public report link removed.',
    });
  };

  onRebuild = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    this.setState({
      rebuilding: true,
      rebuildBuildId: null,
    });
    const { params } = this.props;
    const revisionString = utils.nuc(`${params.project_id}$${params.revision_id}`);
    const url = `/api/revisions/${revisionString}/rebuild`;
    return get(url).then((resp) => {
      const [build] = resp.data;
      return this.setState({
        rebuildBuildId: build.id,
      });
    });
  };

  onShowNoDependenciesModal = () => {
    this.setState({ showNoDependenciesModal: true });
  };

  onVisitAPIEndpoint = () => {
    if (utils.isAuthed()) {
      const { revision } = this.props;
      const locator = utils.nuc(revision.data.locator);
      return utils.openInNewTab(`/api/revisions/${locator}`);
    }
    //  Show upgrade/no access modal if not authenticated
    this.props.showPlanSelectorModal({ tier: FREE_PLAN_NAME, focusFeature: apiSupportFeature });
    return false;
  };

  render() {
    const status = this.getRevisionStatus();
    const baseUrl = utils.getProjectBaseURLFromParams(this.props.params);
    const { userCanEditProject } = this;

    // Archive uploads are available for 30 days.
    const archiveReanalysisCutoff = new Date();
    archiveReanalysisCutoff.setDate(archiveReanalysisCutoff.getDate() - 30);

    if (!utils.isAuthed() || !utils.isProjectInCurrentOrg(this.props.project?.data?.organizationId)) {
      return <Redirect to={`${baseUrl}/preview`} />;
    }

    return (
      <div className="container margin-top">
        {!this.props.project.status.loading && this.props.project.data?.invalidCredential &&
          <InvalidCredentials
            project={this.props.project}
            router={this.context.router}
          />}
        {['failing', 'analyzed'].includes(status)
          && !this.props.project.data?.require_mediated_dependencies
          && this.props.project.data?.type !== ProjectSource.Binary
          && this.props.revision.data
          && !this.props.revision.data.cliLicenseScanType
          && !this.props.revision.data.latestHubbleAnalysisId
          && !this.props.revision.data.dependency_count
          && !(
            // Archive uploads may be automatically removed after a period of time and can't be reanalyzed.
            // Don't show the stale-revision banner for archives that are past expiration.
            // TODO(CORE-2011): Remove this check once archives are no longer automatically removed.
            this.props.project.data?.locator?.startsWith('archive')
            && this.props.revision.data.createdAt
            && new Date(this.props.revision.data.createdAt) < archiveReanalysisCutoff
          )
          &&
          <Alert
            bsStyle="error"
            style={{ padding: '15px', display: 'flex', alignItems: 'baseline', cursor: 'pointer' }}
            onClick={this.onRebuild}
          >
            This revision has stale data and requires a rebuild. Click here to rebuild this revision.
          </Alert>}
        {status === 'loading' && <Loader />}
        {status !== 'loading' && (
          <div key={`summary-ref-${this.props.params.reference}`}>
            {['failing', 'analyzed'].includes(status) ?
              <AnalyzedRevision
                params={this.props.params}
                project={this.props.project}
                revision={this.props.revision}
                baseUrl={baseUrl}
                router={this.context.router}
                userCanEditProject={userCanEditProject}
                rebuilding={this.state.rebuilding}
                onScanIssues={this.props.onScanIssues}
                onVisitAPIEndpoint={this.onVisitAPIEndpoint}
                onShowNoDependenciesModal={this.onShowNoDependenciesModal}
                onGeneratePublicAttributionLink={this.generatePublicAttributionLink}
                onDeletePublicAttributionLink={this.deletePublicAttributionLink}
                onRebuild={this.onRebuild}
              />
              :
              <ProjectSummaryBuildInfo
                vcsHost={this.props.project.data.vcs_host}
                defaultBranch={this.props.project.data?.default_branch || ''}
                isCLIBuild={!!this.props.project.data?.require_mediated_dependencies}
                revisionLocator={this.props.revision.data?.locator || ''}
                revisionMessage={this.props.revision.data?.message || ''}
                status={status}
                userCanEditProject={userCanEditProject}
                onProjectChange={this.props.onProjectChange}
                reference={this.props.params.reference}
                revisionId={this.props.params.revision_id}
                projectId={this.props.params.project_id}
                revision={this.props.revision}
              />}
          </div>
        )}
        <ModalNoDependenciesFound
          project={this.props.project}
          revision={this.props.revision}
          languageDocs={LanguageDocs}
          show={this.state.showNoDependenciesModal}
          onHide={() => this.setState({ showNoDependenciesModal: false })}
        />
        {!this.props.project.data?.require_mediated_dependencies && (
          <ModalStreamBuild
            key="rebuild-modal-stream-build"
            show={this.state.rebuilding}
            buildId={this.state.rebuildBuildId}
            refId={this.props.params.reference}
            revisionId={this.props.params.revision_id}
            onHide={this.onHideModalStreamBuild}
            isProjectLocator
          />
        )}
      </div>
    );
  }
}

/**
 * Container component that provides context values to ProjectSummary.
 * Injects onAddToast, showPlanSelectorModal, baseUrl, canUserEditProject, and onScanIssues.
 */
const ProjectSummaryContainer: VoidFunctionComponent<Omit<ProjectSummaryProps, 'onAddToast' | 'baseUrl' | 'canUserEditProject' | 'showPlanSelectorModal' | 'onScanIssues'>> = (props) => {
  const onAddToast = useAddToast();
  const { showPlanSelectorModal } = usePlanSelectorContext();
  const isLocator = !!props.project?.data?.locator;
  const canEdit = useCanUserEditProject(props.project?.data?.locator ?? '');
  const canUserEditProject = isLocator && canEdit;
  const baseUrl = utils.getProjectBaseURLFromParams(props.params);

  const { mutate: scanRevision } = useScanRevision();

  const onScanIssues = (event: MouseEvent<HTMLElement>) => {
    if (!utils.isAuthed()) {
      showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }
    event.stopPropagation();
    const revisionLocator = props.revision?.data?.locator ?? '';

    // Show info toast when scan starts
    onAddToast({
      type: 'info',
      content: 'Running policy scan.',
    });

    // Using mutate (not mutateAsync) since useToastedMutation handles success/error in callbacks
    scanRevision(revisionLocator);
  };

  if (!utils.isAuthed() || !utils.isProjectInCurrentOrg(props.project?.data?.organizationId)) {
    return <Redirect to={`${baseUrl}/preview`} />;
  }

  if (props.project?.data?.type === ProjectSource.SBOM) {
    return (
      <SBOMSummary
        onProjectChange={props.onProjectChange}
        project={props.project}
        revision={props.revision}
        params={props.params}
        location={props.location}
        onAddToast={onAddToast}
        baseUrl={baseUrl}
        canUserEditProject={canUserEditProject}
        onScanIssues={onScanIssues}
      />
    );
  }

  return (
    <ProjectSummary
      // eslint-disable-next-line react/jsx-props-no-spreading -- It's okay as this is a higher-order component
      {...props}
      onAddToast={onAddToast}
      showPlanSelectorModal={showPlanSelectorModal}
      baseUrl={baseUrl}
      canUserEditProject={canUserEditProject}
      onScanIssues={onScanIssues}
    />
  );
};

export default ProjectSummaryContainer;

const InvalidCredentials = ({ project, router }: {project: ProjectDashboardProject, router: InjectedRouter}) => {
  const { onConfirm } = useDialogContext();
  const gitServiceName = project?.data?.vcs_host ? getGitServiceName(project.data.vcs_host) : '';

  if (project?.data?.vcs_host) {
    return (
      <Alert
        bsStyle="clickable-error"
        style={{ padding: '15px', display: 'flex', alignItems: 'baseline', cursor: 'pointer' }}
        onClick={utils.containEvent(() => {
          onConfirm({
            title: `Reset ${gitServiceName} Credentials`,
            body: (
              <div className="text-center">
                <p>This will replace the current saved credentials for this { gitServiceName } project</p>
                <p>
                  <b>Are you sure you want to do this?</b> (cannot be undone)
                </p>
              </div>
            ),
            onConfirm: () => {
              utils.hardRedirectTo(
                getAuthorizeServiceURL({
                  vcsHost: project.data.vcs_host,
                  location: window.location.href,
                  projectLocator: project.data?.locator,
                  isPrivateAccess: true,
                }),
              );
            },
          });

          return false;
        })}
      >
        <span style={{ fontSize: '18px', flex: 1 }}>This project has lost its connection to {gitServiceName}. <b>Click here</b> to reconnect.</span>
        <span style={{ fontSize: '23px' }}>&raquo;</span>
      </Alert>
    );
  }
  return (
    <Alert
      bsStyle="clickable-error"
      style={{ padding: '15px', display: 'flex', alignItems: 'baseline', cursor: 'pointer' }}
      onClick={utils.containEvent(() => {
        router.push(`/projects/${utils.nuc(project.data.locator)}/settings`);
      })}
    >
      <span style={{ fontSize: '18px', flex: 1 }}>This project has lost its connection to your repository. <b>Click here</b> to reconfigure.</span>
      <span style={{ fontSize: '23px' }}>&raquo;</span>
    </Alert>
  );
};

type AnalyzedRevisionProps = Pick<ProjectSummaryProps, 'project' | 'revision' | 'params'> & {
  router: InjectedRouter;
  baseUrl: string;
  rebuilding: boolean;
  userCanEditProject: boolean;
  onVisitAPIEndpoint: () => void;
  onScanIssues: (event: MouseEvent<HTMLElement>) => void;
  onRebuild: (event: MouseEvent | KeyboardEvent) => void;
  onShowNoDependenciesModal: () => void;
  onGeneratePublicAttributionLink: () => void;
  onDeletePublicAttributionLink: () => void;
};

const AnalyzedRevision = ({
  params,
  project,
  revision,
  router,
  baseUrl,
  rebuilding,
  userCanEditProject,
  onScanIssues,
  onVisitAPIEndpoint,
  onShowNoDependenciesModal,
  onGeneratePublicAttributionLink,
  onDeletePublicAttributionLink,
  onRebuild,
}: AnalyzedRevisionProps) => {
  const { onConfirm } = useDialogContext();
  const isScanned = revision?.data?.meta?.[0]?.last_scan;
  const publicReportURL = `${window.location.origin}/reports/${project.data?.bom_public_id}`;

  const isOnDefaultBranch = params.reference === project.data?.default_branch;
  const bomPublicId = project.data?.bom_public_id;

  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  const onConfirmRegeneratePublicAttributionLink = () => onConfirm({
    title: 'Are you sure you want to regenerate the public report link?',
    body: 'Your previous link will no longer be valid, and anyone with the old link will no longer be able to access the public report.',
    onConfirm: onGeneratePublicAttributionLink,
    confirmText: 'Regenerate Link',
    confirmBtnType: 'primary',
  });

  const onConfirmDeletePublicAttributionLink = () => onConfirm({
    title: 'Are you sure you want to delete this public report link?',
    body: 'Your previous link will no longer be valid, and anyone with the old link will no longer be able to access the public report. There will be no publicly accessible report for this project.',
    onConfirm: onDeletePublicAttributionLink,
    confirmText: 'Delete Link',
    confirmBtnType: 'error',
  });

  return (
    <div className="animated fadeInUp quick project_info">
      <div className="row project_info__container">
        <div className="col-md-8 project_info__summary">
          <div className="panel panel-flat project_info__issues_panel">
            <ProjectSummaryIssues
              project={project}
              revision={revision}
              params={params}
              onScanIssues={onScanIssues}
            />
          </div>
          {isOnDefaultBranch && (
            <div className="card project_report">
              <ReportLinkHeader project={project} />
              {!!bomPublicId && (
                <div className="margin-bottom-sm">
                  <span className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={publicReportURL}
                      onFocus={(ev) => ev.target.select()}
                    />
                    <span className="input-group-addon">
                      <Link to={publicReportURL} openInNewTab>
                        <Icon icon={icons.externalLink} />
                      </Link>
                    </span>
                  </span>
                </div>
              )}
              <Tooltip
                id="generate-public-attribution-link-tooltip"
                showTooltip={!userCanEditProject}
                tooltip="You are not permitted to edit this project."
              >
                <div className="inline-block">
                  <ButtonGroup>
                    <PaidFeatureGate isRestricted={isOrgFreeTier} hidePill>
                      <Button
                        onClick={
                          bomPublicId ? onConfirmRegeneratePublicAttributionLink : onGeneratePublicAttributionLink
                        }
                        isDisabled={!userCanEditProject}
                        type="secondary"
                      >
                        {bomPublicId ? 'Regenerate' : 'Generate'} Link
                        {isOrgFreeTier && (
                          <span>
                            {' '}
                            <Icon icon={icons.lock} />
                          </span>
                        )}
                      </Button>
                    </PaidFeatureGate>
                    {!!bomPublicId && (
                      <Button
                        onClick={onConfirmDeletePublicAttributionLink}
                        isDisabled={!userCanEditProject}
                        type="error"
                      >
                        Delete Link
                      </Button>
                    )}
                  </ButtonGroup>
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="col-md-4 project_info__action_cards">
          <StandardProjectSummaryActionCards
            project={project}
            revision={revision}
            revisionBaseUrl={baseUrl}
            isScannedForIssues={isScanned}
            isRebuilding={rebuilding}
            onRebuild={onRebuild}
            onShowNoDependenciesModal={onShowNoDependenciesModal}
            onVisitAPIEndpoint={onVisitAPIEndpoint}
            routerPush={router.push}
          />
          <Flex direction="column" align="stretch" justify="flex-start" gap={16}>
            <TeamAccessCard projectLocator={project.data?.locator} isLoading={project.status.loading} />
            <LabelsCard
              labels={project.data?.labels?.map((l) => l.label)}
              projectLocator={project.data?.locator}
              isLoading={project.status.loading}
            />
            <ReleaseGroupsCard projectLocator={project.data?.locator} isLoading={project.status.loading} />
          </Flex>
        </div>
      </div>
      <div className="row">
        <DocsLinkForRevision project={project} revision={revision} />
      </div>
    </div>
  );
};

const ReportLinkHeader = ({ project }: {project: ProjectDashboardProject}) => {
  const hasSecurity = useIsSecurityEnabled();
  return (
    <div className="project_report__header">
      <Text.H4>
        Live Project {hasSecurity ? 'Compliance ' : ''}Report
      </Text.H4>
      <Text.Paragraph>
        This link will always show the latest {hasSecurity ? 'compliance ' : ''}report for the {project.data.default_branch} branch of this project
      </Text.Paragraph>
    </div>
  );
};

const DocsLinkForRevision = (
  { project, revision }: { project: ProjectDashboardProject, revision: ProjectDashboardRevision },
) => {
  let title;
  let link;

  if (isContainerProject(project.data?.type)) {
    title = 'Container Scanning';
    link = 'container-scanning';
  } else if (project.data?.require_mediated_dependencies) {
    title = 'Provided Builds';
    link = 'provided-builds';
  } else if (LanguageDocs[revision.data.source_type]) {
    title = LanguageDocs[revision.data.source_type].name;
    link = LanguageDocs[revision.data.source_type].slug;
  }

  return (title && link) ? (
    <p className="subtitle text-center">
      <DocsLink to={link}>
        <Icon icon={icons.review} /> Learn more about <b>{title}</b>.
      </DocsLink>
    </p>
  ) : null;
};
